import { useMemo, useState, type ReactNode } from 'react'
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, Search, SearchX } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { BulkActionBar, type BulkKind } from '@/components/tables/bulk-action-bar'
import { IssueContextMenu } from '@/components/common/issue-context-menu'
import { matchesFilter, parseFilter } from '@/lib/filters'
import { isModifiedClick } from '@/lib/issue-url'
import { fieldByName, useIssueFields } from '@/lib/query'
import { currentSprint } from '@/lib/sprint'
import { useMe } from '@/hooks/use-me'
import { useUiState } from '@/hooks/use-ui-state'
import type { WorkIssue } from '@/types/github'

/** Per-column presentation, read off `columnDef.meta`. */
export type ColMeta = { headClassName?: string; cellClassName?: string }

/**
 * The one issue table. Every issue list (open lists, the verify queue, Health)
 * configures columns + features here rather than re-implementing the TanStack
 * shell — so row click → Sheet, the context menu, selection + the bulk bar,
 * sortable headers, grouping, empty state and skeleton live in exactly one place.
 *
 * - `sortable` turns header cells into sort toggles (per-column `enableSorting`).
 * - `groupBy` groups rows under a (hidden) column; `renderGroupRow` draws the
 *   group header row.
 * - `bulkKind` adds the selection checkbox column + the bulk action bar.
 */
export function IssueDataTable({
  issues,
  columns,
  sortable = false,
  initialSorting,
  groupBy,
  renderGroupRow,
  bulkKind,
  searchable = true,
  emptyTitle = 'No matching issues',
  emptyHint,
}: {
  issues: WorkIssue[]
  columns: ColumnDef<WorkIssue>[]
  sortable?: boolean
  initialSorting?: SortingState
  groupBy?: string
  renderGroupRow?: (row: Row<WorkIssue>, colSpan: number) => ReactNode
  bulkKind?: BulkKind
  /** Show a filter box (same syntax as saved searches — `stage:`, `label:`,
   *  `"text"`). On by default; pass false where a richer filter UI exists (/q). */
  searchable?: boolean
  emptyTitle?: string
  emptyHint?: string
}) {
  const { setSheetIssue } = useUiState()
  const me = useMe()
  const fields = useIssueFields().data
  const sprintOptions = useMemo(
    () => fieldByName(fields, 'Sprint')?.options.map((o) => o.name) ?? [],
    [fields],
  )
  // So `assignee:@me` / `sprint:@current` resolve in the quick-filter (same ctx
  // /q builds for the saved-search bar).
  const filterContext = useMemo(
    () => ({ viewerLogin: me?.login, currentSprintName: currentSprint(sprintOptions)?.name ?? null }),
    [me?.login, sprintOptions],
  )
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? [])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [search, setSearch] = useState('')

  // Local quick-filter: the saved-search engine evaluated client-side over the
  // rows already in the table (no refetch). Grouping/sorting/selection all run
  // on the filtered set.
  const filtered = useMemo(() => {
    const q = search.trim()
    if (!q) return issues
    const parsed = parseFilter(q)
    return issues.filter((issue) => matchesFilter(issue, parsed, filterContext))
  }, [issues, search, filterContext])

  const allColumns = useMemo<ColumnDef<WorkIssue>[]>(() => {
    if (!bulkKind) return columns
    const selectColumn: ColumnDef<WorkIssue> = {
      id: '__select',
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllRowsSelected()
              ? true
              : table.getIsSomeRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={(v) => table.toggleAllRowsSelected(!!v)}
          aria-label="Select all"
        />
      ),
      // Stop propagation so ticking a row doesn't also open the Sheet.
      cell: ({ row }) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(!!v)}
            aria-label={`Select #${row.original.number}`}
          />
        </span>
      ),
      meta: { headClassName: 'w-8 pr-0', cellClassName: 'w-8 pr-0' } satisfies ColMeta,
    }
    return [selectColumn, ...columns]
  }, [columns, bulkKind])

  const table = useReactTable({
    data: filtered,
    columns: allColumns,
    getRowId: (issue) => issue.id,
    state: { sorting, rowSelection },
    enableRowSelection: !!bulkKind,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(groupBy
      ? {
          getGroupedRowModel: getGroupedRowModel(),
          getExpandedRowModel: getExpandedRowModel(),
          // grouping/expanded never change; columnVisibility hides the grouping
          // column so the generic header/cell render naturally excludes it.
          initialState: { grouping: [groupBy], expanded: true, columnVisibility: { [groupBy]: false } },
        }
      : {}),
  })

  const selected = table.getSelectedRowModel().rows.map((r) => r.original)
  const colSpan = table.getVisibleLeafColumns().length

  return (
    <div className="border-border bg-card m-4 mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border">
      {bulkKind && selected.length > 0 && (
        <BulkActionBar issues={selected} kind={bulkKind} onClear={() => setRowSelection({})} />
      )}
      {searchable && (
        <div className="border-border relative border-b px-3 py-1.5">
          <Search className="text-faint-foreground pointer-events-none absolute top-1/2 left-5 size-3.5 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={'Filter… (e.g. stage:Backlog label:is/security "text")'}
            className="h-7 border-0 bg-transparent pl-7 text-xs shadow-none focus-visible:ring-0"
            aria-label="Filter issues"
          />
        </div>
      )}
      <div className="scroll-y flex-1">
        <Table>
          <TableHeader className="bg-surface sticky top-0 z-10">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={(header.column.columnDef.meta as ColMeta)?.headClassName}
                  >
                    {sortable && header.column.getCanSort() ? (
                      <button
                        type="button"
                        className="hover:text-foreground flex items-center gap-1"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === 'asc' && <ArrowUp className="size-3" />}
                        {header.column.getIsSorted() === 'desc' && <ArrowDown className="size-3" />}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) =>
              row.getIsGrouped() ? (
                renderGroupRow?.(row, colSpan) ?? null
              ) : (
                <IssueContextMenu key={row.id} issue={row.original}>
                  <TableRow
                    data-state={row.getIsSelected() ? 'selected' : undefined}
                    className="focus-within:bg-hover cursor-pointer"
                    // The issue title is a real link (keyboard-focusable, new-tab
                    // capable); a plain click elsewhere on the row opens the Sheet.
                    // Modifier-clicks are left to the browser.
                    onClick={(e) => {
                      if (isModifiedClick(e)) return
                      setSheetIssue(row.original.number)
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={(cell.column.columnDef.meta as ColMeta)?.cellClassName}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                </IssueContextMenu>
              ),
            )}
            {filtered.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={colSpan} className="h-32">
                  <div className="text-muted-foreground flex flex-col items-center gap-1.5 text-xs">
                    <SearchX className="text-faint-foreground size-5" />
                    <span>{search.trim() ? 'No matches' : emptyTitle}</span>
                    {search.trim() ? (
                      <span className="text-faint-foreground text-[11px]">No issues match “{search.trim()}”</span>
                    ) : (
                      emptyHint && <span className="text-faint-foreground text-[11px]">{emptyHint}</span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export function TableSkeleton() {
  return (
    <div className="m-4 mt-3 flex flex-col gap-2">
      {Array.from({ length: 8 }, (_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  )
}
